/**
 * SettingsPanel for a group
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Button, ListGroup, Card } from 'react-bootstrap';
import { GROUPTYPES_SCHOOL, EMPLOYEE_ROLE } from 'containers/User/constants.js';
import { useApi2call } from 'containers/Api2/index.js';
import Switch from 'components/interaction/Switch/index.jsx';
import SelectUsers from 'components/interaction/SelectUsers/index.jsx';
import DeleteButton from 'components/interaction/DeleteButton/index.jsx';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import { idValues } from 'utils/arrays.js';
import messages from '../messages.js';
import useUserRoles from './useUserRoles.js';
import useIsFoundation from '../../../useIsFoundation.js';
import { MEMBERTYPE_STAFF } from '../../constants.js';

const Wrapper = styled(Card)`
  .avatar-image {
    border: 1px solid ${(p) => p.theme.tableBorderColor}
  }
  .color-block {
    width: 32px;
    height: 32px;
  }
  dl {
    dt {
      position: absolute;
      width: 6.5em;
      font-weight: normal;
      text-align: right;
    }
    dd {
      margin-left: 7em;
      margin-bottom: 1em;
    }
  }
  .switch {
    margin-left: 1rem;
  }
`;

function SettingsPanel(props) {
  const { group, onEnable } = props;
  const { owners, working, groupType } = group;
  const users = idValues(owners);
  const [showAddOwner, setShowAddOwner] = useState(false);
  const api2call = useApi2call();
  const roles = useUserRoles();
  const defaultRole = useIsFoundation() ? MEMBERTYPE_STAFF : EMPLOYEE_ROLE;
  const { groupOptions, typeName } = group;
  const handleRemove = (user) => () => {
    const remaining = users.filter((o) => o.id !== user.id || o.roleType !== user.roleType);
    handleUpdate(remaining);
  };
  const handleUpdate = (value) => {
    const options = {
      method: 'PUT',
      url: `sa/groups/${group.id}/owners`,
      selector: `sa/groups/${typeName}/${group.id}/owners`,
      version: 2,
      body: value.map((v) => ({ id: v.id, roleType: v.roleType })),
      success: { ...messages.ownersUpdated },
    };
    api2call(options);
  };
  const fetchOptions = {
    url: `sa/groups/${group.id}/possibleGroupOwners`,
    version: 2,
    typeField: 'type',
  };
  return (
    <Wrapper>
      <Card.Header className="border-0">
        <h4><FormattedMessage {...messages.groupSettings} /></h4>
      </Card.Header>
      <Card.Body>
        <section className="mb-4">
          <div className="d-flex align-items-start">
            <b className="flex-grow-1"><FormattedMessage {...messages.appendParents} /></b>
            <Switch value={groupOptions.appendParents} working={working} optimistic onChange={onEnable('appendParents')} />
          </div>
          <div className="form-text"><FormattedMessage {...messages.appendParentsHelp} /></div>
        </section>
        <section className="mb-4">
          <div className="d-flex align-items-start">
            <b className="flex-grow-1"><FormattedMessage {...messages.showContacts} /></b>
            <Switch value={groupOptions.showContacts} working={working} optimistic onChange={onEnable('showContacts')} />
          </div>
          <div className="form-text"><FormattedMessage {...messages.showContactsHelp} /></div>
        </section>
        <section className="mb-4">
          <div><b><FormattedMessage {...messages.groupOwners} /></b></div>
          <div className="form-text"><FormattedMessage {...messages.groupOwnersHelp} /></div>
          {users.length > 0 && (
            <ListGroup className="my-3">
              {users.map((user) => (
                <ListGroup.Item key={user.id} className="d-flex justify-content-between px-0">
                  <UserAvatar user={user} />
                  <DeleteButton
                    onClick={handleRemove(user)}
                    confirmText={{ ...messages.confirmRemoveOwner, values: user }}
                    className="py-0 pe-0"
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
          <div><Button variant="light" onClick={() => setShowAddOwner(true)}><FormattedMessage {...messages.addOwner} /></Button></div>
          <SelectUsers
            show={showAddOwner}
            fetchOptions={fetchOptions}
            value={users}
            exclusions={owners.list}
            onChange={handleUpdate}
            onHide={() => setShowAddOwner(false)}
            roles={roles}
            defaultRole={defaultRole}
          />
        </section>
      </Card.Body>
    </Wrapper>
  );
}

SettingsPanel.propTypes = {
  group: PropTypes.object,
  onEnable: PropTypes.func,
};

export default SettingsPanel;
